import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { toEasternDateShort } from '../utils/time';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All escalations' },
  { value: 'unresolved', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
];
const CAT_COLORS = { MEDICAL: 'danger', LEGAL: 'danger', ABUSE: 'danger', COMPLAINT: 'warning', PAYMENT: 'warning', PHI: 'danger', CRISIS: 'danger', INSURANCE: 'warning', SPAM: '', OTHER: '' };

export default function EscalationsPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() =>
    api.getEscalations()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => toast('Failed to load escalations', 'error'))
      .finally(() => setLoading(false)), [toast]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      await api.resolveEscalation(id);
      setItems(prev => prev.filter(x => x.escalation_id !== id));
      toast('Marked as resolved', 'success');
    } catch (e) {
      toast(e.message || 'Failed', 'error');
    }
  };

  const tc = useTableControls({
    items,
    searchFields: (e) => [e.author_name, e.content_summary, e.classification, e.source_type],
    statusOf: (e) => (e.resolved === 'true' ? 'resolved' : 'unresolved'),
    sorters: {
      timestamp: (e) => e.timestamp || '',
      classification: (e) => e.classification || '',
      status: (e) => (e.resolved === 'true' ? 'resolved' : 'unresolved'),
    },
    defaultSort: 'timestamp',
    defaultDir: 'desc',
    pageSize: 10,
  });

  return (
    <>
      <div className="page-header">
        <h1>Escalations</h1>
      </div>

      {!loading && items.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{items.length}</span>
            <span className="summary-label">Total escalations</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item attention">
            <span className="summary-value">{tc.counts.unresolved || 0}</span>
            <span className="summary-label">Open</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.resolved || 0}</span>
            <span className="summary-label">Resolved</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>All escalations</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search escalations…" />
            <button type="button" className="icon-btn" onClick={load} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs tabs={STATUS_TABS} active={tc.activeStatus} onChange={tc.setActiveStatus} counts={tc.counts} total={items.length} />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <p>No escalations</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No escalations match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="timestamp" label="Date" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Source</th>
                  <SortableTh col="classification" label="Category" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>From</th>
                  <th>Summary</th>
                  <SortableTh col="status" label="Status" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(e => (
                  <tr key={e.escalation_id}>
                    <td className="td-mono">{e.timestamp ? toEasternDateShort(e.timestamp) : '—'}</td>
                    <td><span className={`badge badge-${e.source_type}`}>{e.source_type}</span></td>
                    <td>
                      <span className={`badge badge-${CAT_COLORS[e.classification] || 'draft'}`}>
                        {e.classification}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{e.author_name || '—'}</td>
                    <td className="td-truncate" style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                      {e.content_summary}
                    </td>
                    <td>
                      <span className={`badge badge-dot ${e.resolved === 'true' ? 'badge-resolved' : 'badge-escalated'}`}>
                        {e.resolved === 'true' ? 'Resolved' : 'Open'}
                      </span>
                    </td>
                    <td>
                      {e.resolved !== 'true' && (
                        <button className="btn btn-ghost btn-sm" onClick={() => resolve(e.escalation_id)}>
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
